import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ShareMenu } from "@/components/ShareMenu";
import { ASSETS } from "@/lib/constants";
import { motion } from "framer-motion";
import { Vote, Scale, ShieldCheck, HandCoins, Landmark, BookOpen, Users, CheckCircle2, ArrowRight, Megaphone } from "lucide-react";

const FADE = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

const WHY = [
  {
    icon: HandCoins,
    t: "Reparative Justice",
    d: "Federal appropriations, study commissions, and reparations legislation live and die in Congress. Candidates who understand the moral and legal case for repair are essential to advancing our people's long-standing claims.",
  },
  {
    icon: Scale,
    t: "Enforcement of Our Rights",
    d: "Civil-rights enforcement, judicial appointments, and oversight of federal agencies determine whether the equal protection of the laws is real or symbolic. Aligned lawmakers shape who enforces the law — and how.",
  },
  {
    icon: ShieldCheck,
    t: "Treaty & Human-Rights Compliance",
    d: "Bringing the United States into compliance with ICERD, the ICCPR, and its obligations to peoples of African descent requires legislators willing to hold the federal government accountable to the treaties it has ratified.",
  },
  {
    icon: Landmark,
    t: "Economic Development",
    d: "Access to capital, community investment, contracting, and infrastructure funding flows through federal policy. Candidates aligned with our objectives can direct resources toward the flourishing of our communities.",
  },
];

const CRITERIA = [
  { t: "Record, Not Rhetoric", d: "Evaluate what a candidate has actually done — votes cast, bills sponsored, coalitions built — over campaign-season promises." },
  { t: "Reparations & Repair", d: "Does the candidate acknowledge the historical harm to our people and support concrete, lawful measures toward repair?" },
  { t: "Civil & Human Rights", d: "Consistent defense of voting rights, equal protection, and the enforcement of anti-discrimination law at every level of government." },
  { t: "Economic Justice", d: "Support for community investment, fair access to capital, and policies that build generational wealth in our communities." },
  { t: "Accountability & Access", d: "A demonstrated willingness to meet with, listen to, and answer directly to our communities — not only during election season." },
  { t: "Integrity & Independence", d: "Courage to stand on principle and represent constituents faithfully, rather than defer reflexively to party or donor interests." },
];

const ENGAGE = [
  { icon: BookOpen, t: "Educate", d: "Study candidates' records, attend forums, and share nonpartisan voter-education resources so members can make informed decisions." },
  { icon: Vote, t: "Register & Vote", d: "Ensure every eligible member is registered and casts an informed ballot in federal, state, and local elections." },
  { icon: Megaphone, t: "Advocate Lawfully", d: "Submit questionnaires, request meetings, testify, and hold candidates accountable to our values through lawful, peaceful civic participation." },
  { icon: Users, t: "Organize", d: "Build coalitions, mobilize communities, and turn shared values into collective, sustained political power." },
];

// Candidate endorsement graphics (full 1:1 designed cards).
const ENDORSEMENTS = [
  { slug: "butch-ware", name: "Butch Ware", office: "California Governor", img: "https://customer-assets.emergentagent.com/job_black-american-gov/artifacts/eaqsdj5n_ChatGPT%20Image%20Jul%204%2C%202026%2C%2012_58_43%20PM.png" },
  { slug: "abdul-el-sayed", name: "Abdul El-Sayed", office: "U.S. Senate — Michigan", img: "https://customer-assets.emergentagent.com/job_black-american-gov/artifacts/f4rpq3uj_ChatGPT%20Image%20Jul%204%2C%202026%2C%2012_58_04%20PM.png" },
  { slug: "keisha-lance-bottoms", name: "Keisha Lance-Bottoms", office: "U.S. Senate — Georgia", img: "https://customer-assets.emergentagent.com/job_black-american-gov/artifacts/r6fhgmrh_ChatGPT%20Image%20Jul%204%2C%202026%2C%2012_57_48%20PM.png" },
  { slug: "diop-harris-ii", name: "Diop Harris II", office: "U.S. Congress — Michigan's 4th District", img: "https://customer-assets.emergentagent.com/job_black-american-gov/artifacts/p8dxbycv_ChatGPT%20Image%20Jul%204%2C%202026%2C%2012_57_29%20PM.png" },
  { slug: "priscilla-williams-till", name: "Priscilla Williams-Till", office: "U.S. Senate — Mississippi", img: "https://customer-assets.emergentagent.com/job_black-american-gov/artifacts/bzv7bb6b_ChatGPT%20Image%20Jul%204%2C%202026%2C%2012_57_04%20PM.png" },
];

export default function PoliticalEngagement() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="tribal-stripe h-1.5 w-full" />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 lg:py-24 grid lg:grid-cols-3 gap-12 items-center">
        <div className="lg:col-span-2">
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-5">Political Engagement</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-semibold tracking-tight leading-[0.98] text-[#F5F3EF]">
            Supporting Our<br />Candidates
          </h1>
          <p className="mt-7 text-lg text-[#A8A29E] leading-relaxed max-w-2xl">
            Our objectives — <span className="text-[#F5F3EF] font-medium">reparative justice, the enforcement of our rights, and the
            economic advancement of our people</span> — are decided in the halls of federal power. Supporting candidates whose
            values and objectives align with those of the Nation is one of the most powerful, lawful tools our people possess.
          </p>
        </div>
        <div className="hidden lg:flex justify-center">
          <img src={ASSETS.seal} alt="National Seal" className="h-56 w-56 object-contain" />
        </div>
      </section>

      {/* Why federal alignment matters */}
      <section className="border-y border-[#D4AF37]/20 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20">
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-3">Why It Matters</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight mb-4">Federal Power Shapes Our Future</h2>
          <p className="text-[#A8A29E] max-w-3xl mb-12">
            The federal government controls the levers that most directly affect our people's advancement. Electing
            representatives whose objectives align with ours is how we turn our Declaration into policy.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {WHY.map((w) => (
              <motion.div key={w.t} {...FADE} className="bg-[#141414] border border-[#D4AF37]/30 p-7 flex gap-5" data-testid="pe-why-card">
                <w.icon className="w-8 h-8 text-[#D4AF37] shrink-0" strokeWidth={1.5} />
                <div>
                  <h3 className="font-serif-display text-2xl text-[#F5F3EF] mb-2">{w.t}</h3>
                  <p className="text-sm text-[#A8A29E] leading-relaxed">{w.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values-alignment criteria */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20">
        <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-3">The Standard</div>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight mb-4">How We Evaluate Alignment</h2>
        <p className="text-[#A8A29E] max-w-3xl mb-12">
          We assess candidates by their alignment with the Nation's values — not by party. The following criteria guide
          our members in judging whether a candidate genuinely advances our objectives.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D4AF37]/15 border border-[#D4AF37]/15">
          {CRITERIA.map((c) => (
            <div key={c.t} className="bg-[#0A0A0A] p-8">
              <CheckCircle2 className="w-5 h-5 text-[#5DBB7A] mb-3" />
              <h3 className="font-serif-display text-2xl text-[#F5F3EF] mb-2">{c.t}</h3>
              <p className="text-sm text-[#A8A29E] leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Endorsements */}
      <section className="border-y border-[#D4AF37]/20 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20">
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-3">Standing Together</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight mb-4">Candidate Endorsements</h2>
          <p className="text-[#A8A29E] max-w-3xl mb-12">
            The Nation recognizes leaders who have demonstrated a genuine commitment to our people's values and objectives.
          </p>

          {ENDORSEMENTS.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {ENDORSEMENTS.map((e) => (
                <motion.div key={e.name} id={e.slug} {...FADE} className="bg-[#141414] border border-[#D4AF37]/30 overflow-hidden scroll-mt-24" data-testid="pe-endorsement-card">
                  <div className="aspect-square w-full overflow-hidden bg-[#0A0A0A] flex items-center justify-center">
                    <img src={e.img} alt={`The Black American Government endorses ${e.name} for ${e.office}`} loading="lazy" className="w-full h-full object-contain" />
                  </div>
                  <div className="p-6 border-t border-[#D4AF37]/20 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-serif-display text-2xl text-[#F5F3EF]">{e.name}</h3>
                      <div className="text-[#D4AF37] text-sm font-medium">{e.office}</div>
                    </div>
                    <ShareMenu
                      url={`${window.location.origin}/political-engagement#${e.slug}`}
                      text={`The Black American Government endorses ${e.name} for ${e.office}`}
                      testId={`share-endorsement-${e.slug}`}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="bg-[#141414] border border-dashed border-[#D4AF37]/30 p-12 text-center" data-testid="pe-endorsements-empty">
              <Vote className="w-10 h-10 text-[#D4AF37]/60 mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-serif-display text-2xl text-[#F5F3EF]">Endorsements Forthcoming</p>
              <p className="text-sm text-[#A8A29E] mt-2 max-w-md mx-auto">
                Endorsed candidates who champion the values and objectives of the Nation will be featured here.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* How we engage lawfully */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20">
        <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-3">Our Approach</div>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight mb-12">Engaging Through Lawful, Peaceful Means</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {ENGAGE.map((e) => (
            <div key={e.t} className="bg-[#141414] border border-[#D4AF37]/25 p-7">
              <e.icon className="w-8 h-8 text-[#5DBB7A] mb-4" strokeWidth={1.5} />
              <h3 className="font-serif-display text-2xl text-[#F5F3EF] mb-2">{e.t}</h3>
              <p className="text-sm text-[#A8A29E] leading-relaxed">{e.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA + Disclaimer */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 pb-20">
        <div className="bg-[#141414] border border-[#D4AF37]/30 p-10 sm:p-14 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F5F3EF] tracking-tight max-w-2xl mx-auto">
            Turn our shared values into political power.
          </h2>
          <p className="text-[#A8A29E] mt-4 max-w-2xl mx-auto">Join the Nation and add your voice to a community organizing for justice, representation, and the advancement of our people.</p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/register" data-testid="pe-register-btn">
              <Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white px-8 py-6">Become a Member <ArrowRight className="ml-2 w-4 h-4" /></Button>
            </Link>
            <Link to="/legal-standing" data-testid="pe-to-legal-btn">
              <Button variant="outline" className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-8 py-6">Our Legal Standing</Button>
            </Link>
          </div>
        </div>
        <p className="text-xs text-[#A8A29E]/70 leading-relaxed mt-8 max-w-4xl mx-auto text-center">
          This page is provided for educational and informational purposes to encourage lawful, peaceful civic
          participation. The Nation is non-partisan and does not endorse any political party. Members are encouraged to
          evaluate candidates on their values and records and to comply with all applicable federal, state, and local
          election laws. This page is not legal advice.
        </p>
      </section>

      <Footer />
    </div>
  );
}
